import React, { useCallback, useMemo, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import ResponsiveImage from "../images/ResponsiveImage";
import { buildProductImageLightboxSlide } from "../../utils/productImages";
import "yet-another-react-lightbox/styles.css";
import "./PortfolioMagazine.css";

const DEFAULT_HEADER = {
  sectionLabel: "Portfolio",
  title: "Portfolio",
  subtitle: "A curated editorial presentation of portfolio work.",
};

function getImages(item) {
  return Array.isArray(item?.images)
    ? item.images.filter((image) => image?.url)
    : [];
}

function buildSlides(images = [], title = "Portfolio image") {
  return images
    .map((image, index) =>
      buildProductImageLightboxSlide(image, {
        alt: image.alt || `${title} ${index + 1}`,
        title: image.caption || title,
        description: image.caption || "",
      }),
    )
    .filter(Boolean);
}

function normalizeText(value = "") {
  return String(value).trim().toLowerCase();
}

function formatLabel(value = "portfolio") {
  const label = String(value || "portfolio").trim().toLowerCase();
  if (label === "jewelry-hire") return "Jewellery Hire";

  return label
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getHeader(items = []) {
  const first = items[0];

  if (!first) return DEFAULT_HEADER;

  const sectionLabel = formatLabel(first.section || "portfolio");

  return {
    sectionLabel,
    title: first.title || `${sectionLabel} Portfolio`,
    subtitle:
      first.subtitle ||
      first.caption ||
      "A curated editorial presentation of portfolio work.",
  };
}

function useLightbox() {
  const [state, setState] = useState({
    open: false,
    index: 0,
  });

  const openAt = useCallback((index) => {
    setState({ open: true, index });
  }, []);

  const close = useCallback(() => {
    setState((current) => ({ ...current, open: false }));
  }, []);

  return {
    open: state.open,
    index: state.index,
    openAt,
    close,
  };
}

function ClickableImage({
  image,
  alt,
  className,
  caption,
  onOpen,
  figureClassName = "",
  sizes,
}) {
  if (!image?.url) return null;

  return (
    <figure className={figureClassName}>
      <button
        type="button"
        className="portfolio-magazine-image-button"
        onClick={onOpen}
        aria-label={`Open image: ${alt}`}
      >
        <ResponsiveImage
          image={image}
          alt={alt}
          className={className}
          sizes={sizes}
          loading="lazy"
          decoding="async"
        />
      </button>

      {caption ? (
        <figcaption className="portfolio-magazine-image-caption">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
}

function getImageSizes(className = "") {
  if (className.includes("featured")) {
    return "(max-width: 980px) calc(100vw - 88px), min(52vw, 680px)";
  }

  if (className.includes("card")) {
    return "(max-width: 980px) calc(100vw - 84px), min(44vw, 560px)";
  }

  return "(max-width: 640px) calc(50vw - 52px), min(16vw, 220px)";
}

function StoryParagraphs({ body = [] }) {
  if (!Array.isArray(body) || body.length === 0) return null;

  return body.map((paragraph, index) => (
    <p key={`${paragraph}-${index}`} className="portfolio-magazine-paragraph">
      {paragraph}
    </p>
  ));
}

function ImageGallery({
  images = [],
  itemTitle,
  onOpen,
  containerClassName,
  imageClassName,
  figureClassName,
  startIndex = 0,
}) {
  if (!images.length) return null;

  return (
    <div className={containerClassName}>
      {images.map((image, index) => (
        <ClickableImage
          key={image.public_id || image.url || index}
          image={image}
          alt={image.alt || `${itemTitle} detail ${index + 1}`}
          className={imageClassName}
          sizes={getImageSizes(imageClassName)}
          caption={image.caption}
          figureClassName={figureClassName}
          onOpen={() => onOpen(startIndex + index)}
        />
      ))}
    </div>
  );
}

function FeaturedStory({ item, pageTitle }) {
  const images = useMemo(() => getImages(item), [item]);
  const slides = useMemo(
    () => buildSlides(images, item?.title),
    [images, item?.title],
  );

  const heroImage = images[0];
  const galleryImages = images.slice(1);
  const lightbox = useLightbox();

  const showTitle =
    item?.title && normalizeText(item.title) !== normalizeText(pageTitle);

  return (
    <>
      <article className="portfolio-magazine-featured">
        <div className="portfolio-magazine-featured-media">
          {heroImage ? (
            <ClickableImage
              image={heroImage}
              alt={heroImage.alt || item?.title || "Featured portfolio image"}
              className="portfolio-magazine-featured-image"
              sizes={getImageSizes("portfolio-magazine-featured-image")}
              caption={heroImage.caption || item?.caption}
              figureClassName="portfolio-magazine-media-figure"
              onOpen={() => lightbox.openAt(0)}
            />
          ) : (
            <div className="portfolio-magazine-image-placeholder">
              No image available
            </div>
          )}
        </div>

        <div className="portfolio-magazine-featured-content">
          {showTitle ? (
            <h2 className="portfolio-magazine-featured-title">{item.title}</h2>
          ) : null}

          <StoryParagraphs body={item?.body} />

          <ImageGallery
            images={galleryImages}
            itemTitle={item?.title || "Portfolio"}
            onOpen={lightbox.openAt}
            startIndex={1}
            containerClassName="portfolio-magazine-inline-gallery"
            imageClassName="portfolio-magazine-inline-gallery-image"
            figureClassName="portfolio-magazine-inline-gallery-item"
          />
        </div>
      </article>

      <Lightbox
        open={lightbox.open}
        close={lightbox.close}
        index={lightbox.index}
        slides={slides}
        plugins={[Zoom]}
        zoom={{
          maxZoomPixelRatio: 1,
          scrollToZoom: true,
        }}
      />
    </>
  );
}

function StoryCard({ item, index }) {
  const images = useMemo(() => getImages(item), [item]);
  const slides = useMemo(
    () => buildSlides(images, item?.title),
    [images, item?.title],
  );

  const leadImage = images[0];
  const galleryImages = images.slice(1);
  const lightbox = useLightbox();

  return (
    <>
      <article className="portfolio-magazine-card">
        <div className="portfolio-magazine-card-header">
          <span className="portfolio-magazine-card-number">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="portfolio-magazine-card-rule" />
        </div>

        {leadImage ? (
          <ClickableImage
            image={leadImage}
            alt={leadImage.alt || item?.title || "Portfolio image"}
            className="portfolio-magazine-card-image"
            sizes={getImageSizes("portfolio-magazine-card-image")}
            caption={leadImage.caption || item?.caption}
            figureClassName="portfolio-magazine-media-figure"
            onOpen={() => lightbox.openAt(0)}
          />
        ) : (
          <div className="portfolio-magazine-image-placeholder small">
            No image available
          </div>
        )}

        <div className="portfolio-magazine-card-content">
          {item?.title ? (
            <h2 className="portfolio-magazine-card-title">{item.title}</h2>
          ) : null}

          {item?.subtitle ? (
            <p className="portfolio-magazine-card-subtitle">{item.subtitle}</p>
          ) : null}

          <StoryParagraphs body={item?.body} />

          <ImageGallery
            images={galleryImages}
            itemTitle={item?.title || "Portfolio"}
            onOpen={lightbox.openAt}
            startIndex={1}
            containerClassName="portfolio-magazine-thumb-row"
            imageClassName="portfolio-magazine-thumb"
            figureClassName="portfolio-magazine-thumb-figure"
          />
        </div>
      </article>

      <Lightbox
        open={lightbox.open}
        close={lightbox.close}
        index={lightbox.index}
        slides={slides}
        plugins={[Zoom]}
        zoom={{
          maxZoomPixelRatio: 1,
          scrollToZoom: true,
        }}
      />
    </>
  );
}

export default function PortfolioMagazine({ items = [] }) {
  const safeItems = Array.isArray(items) ? items.filter(Boolean) : [];

  if (!safeItems.length) return null;

  const [featuredItem, ...restItems] = safeItems;
  const header = getHeader(safeItems);

  return (
    <section className="portfolio-magazine-shell">
      <header className="portfolio-magazine-masthead">
        <div className="portfolio-magazine-masthead-top">
          <span className="portfolio-magazine-section-label">
            {header.sectionLabel}
          </span>
          <span className="portfolio-magazine-edition">{header.edition}</span>
        </div>

        <h1 className="portfolio-magazine-masthead-title">{header.title}</h1>

        {header.subtitle ? (
          <p className="portfolio-magazine-masthead-subtitle">
            {header.subtitle}
          </p>
        ) : null}
      </header>

      <FeaturedStory item={featuredItem} pageTitle={header.title} />

      {restItems.length > 0 ? (
        <div className="portfolio-magazine-grid">
          {restItems.map((item, index) => (
            <StoryCard
              key={item?._id || item?.id || `${item?.title}-${index}`}
              item={item}
              index={index + 1}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
